import * as React from 'react';
import { Link } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
    ColDef,
    FirstDataRenderedEvent,
    GridReadyEvent,
    GridSizeChangedEvent,
    ICellRendererParams,
    IHeaderParams,
    SortChangedEvent,
    ValueGetterParams,
} from 'ag-grid-community';
import { GridWrapper } from '@nkr/components/ag-grid/default-style';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ApiStatus } from '@nkr/utils/api/api-client';
import { epletValidationApi, EpletSummary } from '@nkr/models/hla/validation/eplet';
import { GENE_GROUPS } from './gene-groups';

const flaggedCellClass = (params: { data?: EpletSummary }) =>
    params.data && !params.data.hasOnlyValidatedAlleles ? 'bg-notice' : '';

const countCell = (params: ICellRendererParams) => (
    Number(params.value) > 0
        ? <span className='text-danger'>{params.value}</span>
        : <span className='text-muted'>0</span>
);

export const EpletValidation = () => {
    const [group, setGroup] = React.useState<number | null>(null);
    const summary = epletValidationApi.useGetSummary(group);

    const payload = summary.response?.payload;

    const rowData = React.useMemo<EpletSummary[]>(
        () => [...(payload ?? [])].sort(
            (a, b) => (b.missingCount + b.invalidCount) - (a.missingCount + a.invalidCount)),
        [payload]);

    const failing = rowData.filter(x => !x.hasOnlyValidatedAlleles);

    const columnDefs = React.useMemo<ColDef[]>(() => [
        {
            colId: 'rowNumber',
            headerName: '#',
            headerComponentFramework: () => <div>#</div>,
            sortable: false,
            width: 50,
            pinned: 'left',
            valueGetter: (params: ValueGetterParams) => params.node?.rowIndex! + 1,
        },
        {
            field: 'name',
            headerName: 'Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
            sortable: true,
            width: 120,
            cellRendererFramework: (params: ICellRendererParams) => <strong>{params.value}</strong>,
        },
        {
            field: 'hlaGeneGroup',
            headerName: 'Gene Group',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Gene Group</SortableHeaderComponent>,
            sortable: true,
            width: 130,
        },
        {
            field: 'residue',
            headerName: 'Polymorphic Residue',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Polymorphic Residue</SortableHeaderComponent>,
            sortable: true,
            width: 260,
            cellRendererFramework: (params: ICellRendererParams) => <code>{params.value}</code>,
        },
        {
            field: 'linkedCount',
            headerName: 'Linked',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Linked</SortableHeaderComponent>,
            sortable: true,
            width: 90,
        },
        {
            field: 'hasOnlyValidatedAlleles',
            headerName: 'Valid',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Valid</SortableHeaderComponent>,
            sortable: true,
            width: 80,
            cellClass: flaggedCellClass,
            cellRendererFramework: (params: ICellRendererParams) => (
                params.value
                    ? <span className='text-success' title='Reconciles'>&#10004;</span>
                    : <span className='text-danger' title='Does not reconcile'>&#10008;</span>
            ),
        },
        {
            field: 'missingCount',
            headerName: 'To Add',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>To Add</SortableHeaderComponent>,
            sortable: true,
            width: 90,
            cellClass: flaggedCellClass,
            cellRendererFramework: countCell,
        },
        {
            field: 'invalidCount',
            headerName: 'To Remove',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>To Remove</SortableHeaderComponent>,
            sortable: true,
            width: 110,
            cellClass: flaggedCellClass,
            cellRendererFramework: countCell,
        },
        {
            colId: 'actions',
            headerName: 'Actions',
            headerComponentFramework: () => <div>Actions</div>,
            sortable: false,
            width: 90,
            cellRendererFramework: (params: ICellRendererParams) => (
                <Link to={`/admin/hla/eplet/${params.data.id}`} className='btn btn-primary btn-xs'>View</Link>
            ),
        },
    ], []);

    return (
        <div className='container-fluid' style={{ paddingTop: 20 }}>
            <h2>Eplet Allele Validation</h2>

            <p className='text-muted'>
                Every allele link is derived from the eplet&rsquo;s gene group and polymorphic residue.
                Rows flagged below disagree with that derivation &mdash; open one to review the alleles
                against epregistry before adding or removing links.
            </p>

            <ul className='nav nav-tabs' style={{ marginBottom: 15 }}>
                <li className={group === null ? 'active' : ''}>
                    <a href='#' onClick={e => { e.preventDefault(); setGroup(null); }}>All Groups</a>
                </li>
                {GENE_GROUPS.map(x => (
                    <li key={x.id} className={group === x.id ? 'active' : ''}>
                        <a href='#' onClick={e => { e.preventDefault(); setGroup(x.id); }}>{x.label}</a>
                    </li>
                ))}
            </ul>

            {group === null && summary.status === ApiStatus.Loading && (
                <div className='alert alert-info'>
                    Reconciling every eplet against the full allele table. Filter to a single gene group
                    to make this quicker.
                </div>
            )}

            {summary.status === ApiStatus.Error && (
                <div className='alert alert-danger'>
                    {summary.response?.message ?? 'The eplet reconciliation could not be loaded.'}
                </div>
            )}

            {summary.status === ApiStatus.Loaded && (
                <>
                    <div className={failing.length === 0 ? 'alert alert-success' : 'alert alert-warning'}>
                        {failing.length === 0
                            ? <span>All {rowData.length} eplets reconcile. Nothing to add or remove.</span>
                            : <span><strong>{failing.length}</strong> of {rowData.length} eplets disagree with their residue.</span>}
                    </div>

                    <GridWrapper id='grid-wrapper' className='ag-theme-bootstrap'>
                        <AgGridReact
                            columnDefs={columnDefs}
                            rowData={rowData}
                            headerHeight={30}
                            rowHeight={34}
                            suppressCellSelection={true}
                            suppressMultiSort={true}
                            noRowsOverlayComponentFramework={() => <div>No eplets in this gene group.</div>}
                            onGridReady={(e: GridReadyEvent) => e.api.sizeColumnsToFit()}
                            onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                            onGridSizeChanged={(e: GridSizeChangedEvent) => e.api.sizeColumnsToFit()}
                            onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
                        />
                    </GridWrapper>
                </>
            )}
        </div>
    );
};
